import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import type { SetupValue } from '../../data/types';
import { ALL_FIELDS } from '../../domain/setup/sections';
import { fieldError } from '../../domain/setup/setupRules';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { SectionCard } from '../../ui/primitives';
import { DEAL_DEFAULT_GROUPS, type DefaultsGroup } from './houseDefaultGroups';
import BrandScrapeButton from '../setup/BrandScrapeButton';
import SetupFieldInput from '../setup/SetupFieldInput';
import { BrandingPreview } from '../setup/SetupPreviews';

const GROUPS: DefaultsGroup[] = [
  {
    title: 'House branding',
    description: 'Used for every new fund unless that fund sets its own.',
    keys: ['brand.website', 'brand.colour', 'brand.emailHeader', 'brand.emailButton'],
  },
  {
    title: 'Fund manager',
    description: 'Fides as manager: shown on statements and filled into each new deal.',
    keys: [
      'billing.entity',
      'directors',
      'directors.placeOfBusiness',
      'contact.email',
      'contact.phone',
      'contact.address',
      'contact.website',
      'privacy.url',
    ],
  },
  {
    title: 'Banking',
    description: 'Each trust still gets its own account; these are the usual settings.',
    keys: ['bank.name', 'bank.bsb', 'bank.apca', 'bank.selfBalancing'],
  },
  ...DEAL_DEFAULT_GROUPS,
  {
    title: 'Investor rules',
    description: 'How Fides normally treats investors, copied into each fund.',
    keys: [
      'inv.wholesaleMode',
      'inv.verification',
      'inv.investorBankChange',
      'inv.staffBankChange',
      'inv.entityDocs',
      'inv.suspendAccess',
      'inv.nameCase',
    ],
  },
];

const fieldFor = (key: string) => ALL_FIELDS.find((field) => field.key === key)!;

export default function HouseDefaultsTab() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const saved = state.houseDefaults[scope.tenant.id] ?? {};
  const [draft, setDraft] = useState<Record<string, SetupValue>>(saved);
  const changed = Object.keys(draft).filter((key) => draft[key] !== saved[key]);
  const errors = GROUPS.flatMap((group) => group.keys).filter((key) =>
    fieldError({ ...fieldFor(key), required: false }, draft[key]),
  );
  const update = (patch: Record<string, SetupValue>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const previewValues = {
    ...draft,
    'fund.name': `${scope.tenant.shortName} funds`,
    'brand.loginUrl': 'invest.fidescapital.com.au',
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 340px' },
      }}
    >
      <Stack spacing={2}>
        <Alert severity="info">
          These defaults fill every new deal, so nobody types them again. Each fund can still change
          its own settings during setup. Changes here apply to new deals, not funds already set up.
        </Alert>
        {GROUPS.map((group) => (
          <SectionCard
            key={group.title}
            title={group.title}
            subtitle={group.description}
            action={group.title === 'House branding' ? <BrandScrapeButton /> : undefined}
          >
            <Box
              sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
            >
              {group.keys.map((key) => (
                <SetupFieldInput
                  key={key}
                  field={{ ...fieldFor(key), toConfirm: false, help: undefined }}
                  value={draft[key]}
                  prefill={saved[key]}
                  error={fieldError({ ...fieldFor(key), required: false }, draft[key])}
                  readOnly={false}
                  onChange={(value) => update({ [key]: value })}
                />
              ))}
            </Box>
          </SectionCard>
        ))}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={!changed.length} onClick={() => setDraft(saved)}>
            Discard changes
          </Button>
          <Button
            variant="contained"
            disabled={!changed.length || errors.length > 0}
            onClick={() => {
              dispatch({
                type: 'HOUSE_DEFAULTS_UPDATE',
                patch: Object.fromEntries(changed.map((key) => [key, draft[key]])),
              });
              toast('Fund defaults saved');
            }}
          >
            Save{' '}
            {changed.length
              ? `${changed.length} change${changed.length === 1 ? '' : 's'}`
              : 'changes'}
          </Button>
        </Stack>
      </Stack>
      <Box>
        <Typography variant="overline" color="text.secondary">
          Live preview
        </Typography>
        <BrandingPreview values={previewValues} />
      </Box>
    </Box>
  );
}
